import { IDropdownMenuItem } from "@atomaro/ui-kit/components/DropdownMenu/types";
import { formatISO } from "date-fns";
import { useApiariesStore } from "entities/apiaries";
import { useHivesStore } from "entities/hives"
import { useNotificationState } from "entities/notifications";
import { GetNotificationRequestParams, TNotificationRequestStatus } from "entities/notifications/model/types";
import { useUserStore } from "entities/user";
import { useRef } from "react";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { useDebounceCallback } from "shared/lib/debounce/useDebounce";
import { PaginationResponse } from "shared/types";

type FilterValues = {
  hiveIds: string[];
  dateFrom: Date | null;
  dateTo: Date | null;
}

export const useNotifications = () => {
  const userInfo = useUserStore(state => state.userInfo);
  const hives = useHivesStore(s => s.hives);

  const notifications = useNotificationState(s => s.notifications);
  const getNotificationsFromServer = useNotificationState(s => s.getNotificationsFromServer);
  const getNextPage = useNotificationState(s => s.getNextPage);
  const setNotificationStatusRead = useNotificationState(s => s.setNotificationStatusRead);
  const readAll = useNotificationState(s => s.readAll);
  const pageSize = useNotificationState(s => s.pageSize);
  const currentPage = useNotificationState(s => s.currentPage);
  const counters = useNotificationState(s => s.counters);
  
  const apiaryId = useApiariesStore(state => state.currentApiaryId);

  const status = useRef<TNotificationRequestStatus>('NEW')

  const {
    getValues,
    setValue
  } = useForm<FilterValues>({
    defaultValues: {
      hiveIds: [],
      dateFrom: null,
      dateTo: null
    }
  })

  const getFirstPage = (pageParams?: PaginationResponse) => {
    if (!apiaryId||!userInfo) return;

    const { hiveIds, dateFrom, dateTo } = getValues();

    const params: GetNotificationRequestParams = {
      // apiaryId,
      // apiaryId,
      userId: userInfo.id,
      hiveIds
    };
    if (dateFrom) {
      params.dateFrom = formatISO(dateFrom);
    }
    if (dateTo) {
      params.dateTo = formatISO(new Date(dateTo.getTime()).setHours(23, 59, 59))
    }

    if (status.current) params.status = status.current;
    if (pageParams) {
      params.page = pageParams;
    }

    getNotificationsFromServer(params)
  }

  const getFirstPageDebounced = useDebounceCallback(getFirstPage, 500)

  const onHivesChange = (values: IDropdownMenuItem[]) => {
    setValue('hiveIds', values.map(el=>`${el.key}`))
    getFirstPageDebounced();
  }

  const onDateChange = useDebounceCallback((activeDate?: Date, secondDate?: Date) => {
    setValue('dateFrom', null);
    setValue('dateTo', null);

    if (activeDate) {
      setValue('dateFrom', activeDate)
    }

    if (secondDate) {
      setValue('dateTo', secondDate)
    }

    if (activeDate || secondDate) {
      getFirstPageDebounced({
        index: 0,
        size: pageSize * (currentPage+1)
      })
    }
  }, 200)

  const onChipClick = (type: TNotificationRequestStatus) => {
    status.current = type;
    getFirstPage({
      index: 0,
      size: pageSize * (currentPage+1)
    });
  }

  const onInView = (inView: boolean) => {
    if (!inView) return;

    getNextPage();
  }

  const onItemInView = (id: string, inView: boolean) => {
    if (inView) {
      setNotificationStatusRead(id)
      getFirstPageDebounced({
        index: 0,
        size: pageSize * (currentPage+1)
      })
    }
  }

  const onReadAllClick = async () => {
    const withErrors = [];

    for (let index = 0; index < hives.length; index++) {
      const hive = hives[index];
      const hiveId = hive.id;
      if (!hiveId) continue;

      const res = await readAll(hiveId);

      if (res?.status === 200) {
        continue;
      }

      withErrors.push(hive.number);
    }

    if (!withErrors.length) {
      toast.success('Все уведомления успешно прочитаны');
    } else {
      for (let index = 0; index < withErrors.length; index++) {
        const num = withErrors[index];
        toast.error(`При отметке прочитанного уведомления в улье номер ${num} возникла ошибка, попробуйте повторить позже пожалуйста`);
      }
    }

    getFirstPageDebounced({
      index: 0,
      size: pageSize * (currentPage+1)
    })
  }

  return {
    hives,
    notifications,
    status,
    counters,
    onHivesChange,
    onChipClick,
    onInView,
    onItemInView,
    onReadAllClick,
    onDateChange
  }
}